import type { AxiosError } from 'axios';

type ErrorResponse = {
  errors?: Record<string, unknown>;
};

interface GetServerErrorMessageOptions {
  /**
   * Map of backend error codes to already-localized messages.
   * Typically this will be dict.errorCodes.
   */
  codeMap: Record<string, string | undefined>;
  /**
   * Fallback message if no matching error code is found.
   */
  fallbackMessage: string;
}

/**
 * Extracts the first error code from a typical backend error response
 * of shape `{ errors: { field: 'errorCode' } }`.
 */
export function getFirstServerErrorCode(error: unknown): string | undefined {
  const axiosError = error as AxiosError<ErrorResponse>;
  const data = axiosError?.response?.data;

  if (!data || !data.errors) {
    return undefined;
  }

  const values = Object.values(data.errors);

  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
    if (Array.isArray(value)) {
      const first = value.find(
        (v): v is string => typeof v === 'string' && v.length > 0,
      );
      if (first) return first;
    }
  }

  return undefined;
}

/**
 * Returns a translated error message for a backend error response,
 * based on a mapping from backend error codes to localized messages.
 *
 * Usage:
 *  const message = getServerErrorMessage(error, {
 *    codeMap: dict.errorCodes,
 *    fallbackMessage: dict.errorCodes.unknownError,
 *  });
 */
export function getServerErrorMessage(
  error: unknown,
  options: GetServerErrorMessageOptions,
): string {
  const code = getFirstServerErrorCode(error);

  if (code && options.codeMap[code]) {
    return options.codeMap[code] as string;
  }

  return options.fallbackMessage;
}
